// Card "Negociações" da ficha do cliente (specs/21): abertas com prazo e as fechadas recentes.
import { Link } from 'react-router-dom';
import { data, moeda, num } from '../format';
import { COR_GANHO, COR_PERDIDO, deOutro, fundoEtapa, type ConfigNegociacao, type NegociacaoCliente } from '../negociacao';

const diasDesde = (iso: string) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));

export function CardNegociacoes({ cfg, negs, onPasso, meuId, veTodas }: {
  cfg: ConfigNegociacao; negs: NegociacaoCliente[];
  /** Quem está vendo: negociação de outro vendedor aparece só para consulta (specs/27) */
  meuId?: string; veTodas: boolean;
  /** Abre o Registrar contato já na opção (id da etapa, GANHO ou PERDIDO) e na negociação. */
  onPasso: (opcao: string, oportunidadeId: string) => void;
}) {
  const abertas = negs.filter((n) => n.status === 'ABERTA');
  const fechadas = negs.filter((n) => n.status !== 'ABERTA').slice(0, 5);
  const emAberto = abertas.filter((n) => cfg.etapas.find((e) => e.id === n.etapaId)?.somaPipeline).reduce((a, n) => a + Number(n.valor), 0);
  const hoje = new Date().toISOString().slice(0, 10);

  const linha = (n: NegociacaoCliente) => {
    const e = cfg.etapas.find((x) => x.id === n.etapaId);
    const cor = n.status === 'GANHA' ? COR_GANHO : n.status === 'PERDIDA' ? COR_PERDIDO : e?.cor ?? '#94A3B8';
    const valor = n.status === 'ABERTA' && e?.pedeValor === 'NAO' ? (e.pedeQuantidade ? `${num(n.quantidade ?? 0)} ${(e.rotuloQuantidade ?? 'un.').toLowerCase()}` : '') : moeda(n.valor);
    const vencida = n.status === 'ABERTA' && n.prazoEm && n.prazoEm < hoje;
    const info = n.status === 'ABERTA'
      ? `Aberta há ${diasDesde(n.createdAt)} dias${n.prazoEm ? ` · ${e?.pedeValor === 'NAO' ? 'cobrar em' : 'válido até'} ${data(n.prazoEm)}` : ''}`
      : n.status === 'GANHA' ? `Fechada em ${n.fechadaEm ? new Date(n.fechadaEm).toLocaleDateString('pt-BR') : '—'}`
      : `Perdida em ${n.fechadaEm ? new Date(n.fechadaEm).toLocaleDateString('pt-BR') : '—'} · ${n.motivoPerda ?? ''}`;
    const proxima = n.status === 'ABERTA' ? cfg.etapas[cfg.etapas.findIndex((x) => x.id === n.etapaId) + 1] : undefined;
    const consulta = n.status === 'ABERTA' && deOutro(n, meuId, veTodas);
    const outro = !!n.responsavelId && n.responsavelId !== meuId;
    return (
      <li key={n.id} className="neg-item">
        <div className="col" style={{ gap: 3, minWidth: 0 }}>
          <span className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span className="pilula-neg sm" style={{ background: fundoEtapa(cor), color: cor }}>{n.etapa}</span>
            {valor && <strong className="num" style={{ fontWeight: 500 }}>{valor}</strong>}
            {vencida && <span className="badge amber">{e?.pedeValor === 'NAO' ? 'sem retorno' : 'vencido'}</span>}
            {consulta && <span className="badge" title="Negociação de outro vendedor: você vê, mas quem move é ele ou o administrador">Só consulta</span>}
          </span>
          <span className="small faint">{info}{outro && n.responsavel ? ` · ${n.status === 'ABERTA' ? 'com' : 'de'} ${n.responsavel}` : ''}</span>
        </div>
        {n.status === 'ABERTA' && !consulta && (
          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
            {proxima && <button className="btn sm" onClick={() => onPasso(proxima.id, n.id)}>{proxima.nome}</button>}
            <button className="btn sm" onClick={() => onPasso('GANHO', n.id)}>{cfg.ganho?.nome ?? 'Ganhou'}</button>
            <button className="btn sm" onClick={() => onPasso('PERDIDO', n.id)}>{cfg.perdido?.nome ?? 'Perdeu'}</button>
          </div>
        )}
      </li>
    );
  };

  return (
    <section className="card" aria-labelledby="h-negs-ficha">
      <div className="card-head">
        <h2 id="h-negs-ficha">Negociações</h2>
        <span className="small faint" style={{ marginLeft: 'auto' }}>
          {abertas.length ? `${abertas.length} aberta${abertas.length > 1 ? 's' : ''}${emAberto ? ` · ${moeda(emAberto)} em aberto` : ''}` : 'nenhuma aberta'}
        </span>
      </div>
      {negs.length ? <ul className="neg-lista">{[...abertas, ...fechadas].map(linha)}</ul>
        : <span className="small muted">Nenhuma negociação ainda. Registre pelo “Registrar contato”.</span>}
      <Link className="small" to="/negociacoes">Ver todas as negociações</Link>
    </section>
  );
}

/** Aviso ao trocar os vendedores do cliente: o que muda de dono ao salvar (specs/27). */
export function AvisoTransferencia({ negs, para }: { negs: NegociacaoCliente[]; para: string }) {
  if (!negs.length) return null;
  const de = [...new Set(negs.map((n) => n.responsavel).filter(Boolean))].join(', ');
  const total = negs.reduce((a, n) => a + Number(n.valor || 0), 0);
  return (
    <div className="aviso-transferencia" role="status">
      <strong>Ao salvar, {negs.length === 1 ? 'a negociação aberta vai junto' : `${negs.length} negociações abertas vão junto`}</strong>
      <span>{negs.length === 1 ? 'A negociação aberta' : 'As negociações abertas'} de {de || 'quem deixa de atender'}{total ? ` (${moeda(total)})` : ''} neste cliente {negs.length === 1 ? 'passa' : 'passam'} para <b>{para}</b>: {negs.length === 1 ? 'aparece' : 'aparecem'} no quadro e no pipeline dele(a).</span>
      <span className="small">As já fechadas (vendidas ou perdidas) continuam com quem fechou.</span>
    </div>
  );
}
